import type {
  CompactMetricEnvelope,
  CompactRdMetrics,
  MetricChip,
  MetricName,
} from "../../../shared/metrics";
import { metricReasonText } from "../../../shared/metrics";
import {
  isRosterRowNotPublished,
  type PublishedRosterRow,
  type RosterMetricsRow,
} from "./metrics-cache";

/*
 * Pure roster-lane logic for /rds (NO-559). No React and relative imports
 * only, so it loads under `tsx --test`. Decision 1: only the three displayed
 * metrics feed the lane rule and the within-lane sort; every other envelope
 * on the RD is ignored here.
 */

export type RosterMetricName = "billableHours" | "forwardLoad" | "retention";

export interface RosterMetricColumn {
  name: RosterMetricName;
  label: string;
}

export const ROSTER_METRICS: readonly RosterMetricColumn[] = [
  // NO-667: the hours column reads billable hours against the contract
  // minimum; the frame's label stays.
  { name: "billableHours", label: "Hours vs goal" },
  { name: "forwardLoad", label: "Forward load" },
  { name: "retention", label: "Retention" },
];

export type Lane =
  | "needs-conversation"
  | "watch"
  | "too-early"
  | "on-track"
  | "above"
  | "not-published";

/**
 * `not-published` is always last. It is not a rank: it holds RDs the latest
 * publication does not cover yet, so it sits after every graded lane rather
 * than competing with them (NO-618).
 */
export const LANE_ORDER: readonly Lane[] = [
  "needs-conversation",
  "watch",
  "too-early",
  "on-track",
  "above",
  "not-published",
];

export const LANE_LABELS: Record<Lane, string> = {
  "needs-conversation": "Needs a conversation",
  watch: "Watch",
  "too-early": "Too early",
  "on-track": "On track",
  above: "Above expectations",
  "not-published": "No Data Available",
};

/**
 * The whole explanation, stated once per lane rather than once per row, so a
 * week that onboards four RDs does not repeat one sentence four times. No date:
 * naming the next cutoff needs a publication schedule that does not exist yet.
 */
export const NOT_PUBLISHED_LANE_NOTE = "first metrics in the next weekly batch";

/** The sub-label an uncovered RD's cells carry, in place of a value. */
export const NO_DATA_SUB = "No Data";

/**
 * An uncovered RD has no envelope at all, so its cells draw the bare track:
 * no zones, no fill, no threshold, no fleet marker. The track still renders so
 * the row keeps the column rhythm of every other row.
 */
export const EMPTY_GOAL_BAR: GoalBarModel = {
  scaleMax: 1,
  fillFraction: null,
  goodZone: null,
  watchBand: null,
  threshold: null,
  fleetMedian: null,
  fillTone: null,
};

export interface ChipCounts {
  below: number;
  watch: number;
  tooEarly: number;
  above: number;
}

function rosterChips(metrics: CompactRdMetrics): MetricChip[] {
  return ROSTER_METRICS.map((column) => metrics.metrics[column.name].chip);
}

/** Counts over the three displayed metrics only (Decision 1). */
export function chipCounts(metrics: CompactRdMetrics): ChipCounts {
  const counts: ChipCounts = { below: 0, watch: 0, tooEarly: 0, above: 0 };

  for (const chip of rosterChips(metrics)) {
    if (chip === "Below") counts.below += 1;
    else if (chip === "Watch") counts.watch += 1;
    else if (chip === "Too early") counts.tooEarly += 1;
    else if (chip === "Above") counts.above += 1;
  }

  return counts;
}

/** Worst displayed chip wins; two or more Too early outranks Above and On track. */
export function laneFor(metrics: CompactRdMetrics): Lane {
  const counts = chipCounts(metrics);
  if (counts.below > 0) return "needs-conversation";

  if (counts.watch > 0) return "watch";

  if (counts.tooEarly >= 2) return "too-early";

  if (counts.above > 0) return "above";

  return "on-track";
}

export interface RosterLaneGroup {
  lane: Lane;
  label: string;
  rows: RosterMetricsRow[];
}

function compareRows(a: PublishedRosterRow, b: PublishedRosterRow): number {
  const ca = chipCounts(a.metrics);
  const cb = chipCounts(b.metrics);
  if (ca.below !== cb.below) return cb.below - ca.below;

  if (ca.watch !== cb.watch) return cb.watch - ca.watch;

  return a.name.localeCompare(b.name);
}

/**
 * Lanes in LANE_ORDER, empty lanes omitted. Graded lanes sort Below desc, Watch
 * desc, name; the terminal not-published lane has no chips to rank, so it sorts
 * by name alone.
 */
export function groupRosterIntoLanes(
  rows: readonly RosterMetricsRow[]
): RosterLaneGroup[] {
  const graded = new Map<Lane, PublishedRosterRow[]>();
  const notPublished: RosterMetricsRow[] = [];

  for (const row of rows) {
    if (isRosterRowNotPublished(row)) {
      notPublished.push(row);
      continue;
    }

    const lane = laneFor(row.metrics);
    const bucket = graded.get(lane) ?? [];
    bucket.push(row);
    graded.set(lane, bucket);
  }

  return LANE_ORDER.flatMap((lane): RosterLaneGroup[] => {
    if (lane === "not-published") {
      if (notPublished.length === 0) return [];

      return [
        {
          lane,
          label: LANE_LABELS[lane],
          rows: [...notPublished].sort((a, b) => a.name.localeCompare(b.name)),
        },
      ];
    }

    const laneRows = graded.get(lane);
    if (!laneRows || laneRows.length === 0) return [];

    return [{ lane, label: LANE_LABELS[lane], rows: [...laneRows].sort(compareRows) }];
  });
}

// --- Cell values --------------------------------------------------------------

export const EM_DASH = "—";

export interface CellValue {
  display: string;
  sub: string | null;
}

/** Forward load is shown as percent of its absolute target; null when it has none. */
export function forwardLoadPercent(envelope: CompactMetricEnvelope): number | null {
  return envelope.percentOfTarget === null
    ? null
    : Math.round(envelope.percentOfTarget);
}

function formatValue(
  name: MetricName,
  envelope: CompactMetricEnvelope,
  value: number
): string {
  if (name === "billableHours") return value.toFixed(1);

  if (name === "retention") return `${Math.round(value)}%`;

  if (name === "forwardLoad") {
    const percent = forwardLoadPercent(envelope);

    return percent === null ? `${value.toFixed(1)} hrs` : `${percent}%`;
  }

  return value.toFixed(1);
}

/** Chip-driven: a Too early envelope renders the dash even when it carries a value. */
export function cellValue(
  name: MetricName,
  envelope: CompactMetricEnvelope
): CellValue {
  if (envelope.tooEarly) {
    const reason = metricReasonText(envelope.reason);
    const sub = reason ? `Too early · ${reason}` : "Too early";

    return { display: EM_DASH, sub };
  }

  if (envelope.value === null)
    return {
      display: EM_DASH,
      sub: metricReasonText(envelope.reason) ?? envelope.chip,
    };

  return { display: formatValue(name, envelope, envelope.value), sub: null };
}

// --- Goal bar model -----------------------------------------------------------

export type FillTone = "below" | "watch" | "meets" | "above";

export interface GoalBarModel {
  scaleMax: number;
  fillFraction: number | null;
  goodZone: [number, number] | null;
  watchBand: [number, number] | null;
  threshold: number | null;
  fleetMedian: number | null;
  fillTone: FillTone | null;
}

/** The largest modelled quantity sits at 84% of the track. */
const SCALE_HEADROOM = 0.84;

function fillToneFor(chip: MetricChip, tooEarly: boolean): FillTone | null {
  if (tooEarly) return null;

  switch (chip) {
    case "Below":
      return "below";
    case "Watch":
      return "watch";
    case "Meets":
      return "meets";
    case "Above":
      return "above";
    default:
      return null;
  }
}

interface BarInputs {
  value: number | null;
  targetMin: number | null;
  targetMax: number | null;
  threshold: number | null;
  watch: { min: number | null; max: number | null } | null;
  fleetMedian: number | null;
}

/** All bars use the producer's quantity unit; only the cell label uses percent-of-target. */
function barInputs(envelope: CompactMetricEnvelope): BarInputs {
  const target = envelope.good;
  const fleetMedian = envelope.fleet?.median ?? null;

  if (!target) {
    return {
      value: envelope.value,
      targetMin: null,
      targetMax: null,
      threshold: null,
      watch: envelope.watch,
      fleetMedian,
    };
  }

  return {
    value: envelope.value,
    targetMin: target.min,
    targetMax: target.max,
    threshold: target.min ?? target.max,
    watch: envelope.watch,
    fleetMedian,
  };
}

function clampFraction(n: number): number {
  if (Number.isNaN(n)) return 0;

  return Math.min(1, Math.max(0, n));
}

export function goalBarModel(
  _name: MetricName,
  envelope: CompactMetricEnvelope
): GoalBarModel {
  const inputs = barInputs(envelope);
  const candidates = [
    inputs.value,
    inputs.targetMin,
    inputs.targetMax,
    inputs.watch?.max ?? null,
    inputs.fleetMedian,
    1,
  ].filter((n): n is number => n !== null && Number.isFinite(n));
  const scaleMax = Math.max(...candidates) / SCALE_HEADROOM;
  const fraction = (n: number | null): number | null =>
    n === null ? null : clampFraction(n / scaleMax);

  // A band with neither bound is no band; an open bound runs to the track edge.
  const band = (
    b: { min: number | null; max: number | null } | null
  ): [number, number] | null => {
    if (!b || (b.min === null && b.max === null)) return null;

    return [fraction(b.min ?? 0) as number, fraction(b.max ?? scaleMax) as number];
  };

  const fillTone = fillToneFor(envelope.chip, envelope.tooEarly);

  return {
    scaleMax,
    fillFraction: fillTone === null ? null : fraction(inputs.value),
    goodZone: band({ min: inputs.targetMin, max: inputs.targetMax }),
    watchBand: band(inputs.watch),
    threshold: fraction(inputs.threshold),
    fleetMedian: fraction(inputs.fleetMedian),
    fillTone,
  };
}

// --- Rollup strip -------------------------------------------------------------

export interface RollupSummary {
  /** Every RD on the roster, published or not: the true headcount. */
  rdCount: number;
  /** How many of them the latest publication does not cover. */
  notPublishedCount: number;
  hoursAvg: number | null;
  forwardLoadPct: number | null;
  retentionPct: number | null;
}

function mean(values: Array<number | null>): number | null {
  const present = values.filter((n): n is number => n !== null && Number.isFinite(n));
  if (present.length === 0) return null;

  return present.reduce((sum, n) => sum + n, 0) / present.length;
}

/**
 * Decision 2: each figure is the mean of the non-null displayed cell numbers.
 * NO-618: `rdCount` stays the true headcount while the three means are taken
 * over published rows only, so no figure is ever averaged over a denominator
 * that includes an RD with nothing to average.
 */
export function rollupSummary(rows: readonly RosterMetricsRow[]): RollupSummary {
  const published = rows.filter(
    (row): row is PublishedRosterRow => !isRosterRowNotPublished(row)
  );

  const displayed = (
    row: PublishedRosterRow,
    name: RosterMetricName
  ): number | null => {
    const envelope = row.metrics.metrics[name];

    return envelope.tooEarly ? null : envelope.value;
  };

  return {
    rdCount: rows.length,
    notPublishedCount: rows.length - published.length,
    hoursAvg: mean(published.map((row) => displayed(row, "billableHours"))),
    forwardLoadPct: mean(
      published.map((row) => {
        const envelope = row.metrics.metrics.forwardLoad;

        return envelope.tooEarly ? null : forwardLoadPercent(envelope);
      })
    ),
    retentionPct: mean(published.map((row) => displayed(row, "retention"))),
  };
}

/** -> ["20.6 avg", "92%", "94%"], with an em dash for a figure with no inputs. */
export function formatRollup(summary: RollupSummary): [string, string, string] {
  return [
    summary.hoursAvg === null ? EM_DASH : `${summary.hoursAvg.toFixed(1)} avg`,
    summary.forwardLoadPct === null
      ? EM_DASH
      : `${Math.round(summary.forwardLoadPct)}%`,
    summary.retentionPct === null ? EM_DASH : `${Math.round(summary.retentionPct)}%`,
  ];
}

// --- Dates --------------------------------------------------------------------

/**
 * "Aug 22" from an ISO instant, read as its YYYY-MM-DD part so the day never
 * shifts with the viewer's timezone. Local copy of rd-list-row's
 * formatLocalDate, which cannot load under tsx --test.
 */
export function dataThroughLabel(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return iso;

  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * The Roster cell: the true headcount, plus how many RDs are waiting on a
 * publication so the averages beside it cannot be read against the wrong
 * denominator. The clause disappears when nothing is waiting.
 */
export function rosterCountLabel(summary: RollupSummary): string {
  const head = `${summary.rdCount} RD${summary.rdCount === 1 ? "" : "s"}`;
  if (summary.notPublishedCount === 0) return head;

  return `${head} · ${summary.notPublishedCount} not published yet`;
}
